import React, { useState } from 'react';
import { Alert, Button, DatePicker, Empty, Input, Select, Spin, Tag, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcaseMedical, faCalendarCheck, faNoteSticky, faPhoneVolume, faUserCheck } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import MemberCarePlanService from '../../../services/MemberCarePlanService';
import { COLORS } from '../../../constants/colors';
import { Section } from './CarePlanTab';

const { TextArea } = Input;

const STATUS_OPTIONS = [
  { value: 'pending-call', label: 'Needs a call' },
  { value: 'call-attempted', label: 'Call attempted' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'follow-up-scheduled', label: 'Follow-up scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' }
];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'].map((p) => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }));
const NOTE_TYPES = [
  { value: 'general', label: 'Note' },
  { value: 'call-attempt', label: 'Call' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'resolution', label: 'Resolution' },
  { value: 'escalation', label: 'Escalation' }
];
const NOTE_LABELS = Object.fromEntries(NOTE_TYPES.map((t) => [t.value, t.label]));

const fmt = (d) => (d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '');

const card = { border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 18, background: COLORS.$white };
const label = { fontSize: 12, color: COLORS.textLight, marginBottom: 6, fontWeight: 500 };

/** Status, owner, follow-up and notes for the member's open case. */
const CaseTab = ({ patientId, data, loading, error, currentUserId, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [followUpDate, setFollowUpDate] = useState(null);
  const [followUpReason, setFollowUpReason] = useState('');

  if (loading && !data) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  if (error) return <Alert type="warning" showIcon message={error} />;
  if (!data) return null;

  const c = data.case;
  const assignedToMe = c?.assignedTo?.userId && String(c.assignedTo.userId) === String(currentUserId);
  const pendingFollowUp = c?.followUp && !c.followUp.completed ? c.followUp : null;

  const run = async (fn, okText) => {
    setSaving(true);
    try {
      const res = await fn();
      if (!res?.success) {
        message.error(res?.error || 'Something went wrong');
        return false;
      }
      if (okText) message.success(okText);
      onChanged();
      return true;
    } catch (e) {
      message.error('Something went wrong');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const update = (changes, okText) => run(() => MemberCarePlanService.updateCase(patientId, changes), okText);

  const addNote = async () => {
    const ok = await run(() => MemberCarePlanService.addNote(patientId, { text: noteText.trim(), noteType }), 'Note added');
    if (ok) {
      setNoteText('');
      setNoteType('general');
    }
  };

  const scheduleFollowUp = async () => {
    const ok = await update({ followUpDate: followUpDate.toISOString(), followUpReason }, 'Follow-up scheduled');
    if (ok) {
      setFollowUpDate(null);
      setFollowUpReason('');
    }
  };

  return (
    <div style={{ padding: '4px 2px 24px' }}>
      <Section first icon={faBriefcaseMedical} title="Case">
      {!c && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16, borderRadius: 12 }}
          message="No open case"
          description={`${data.lastClosedCase ? `Last case was ${data.lastClosedCase.status} on ${fmtDay(data.lastClosedCase.closedAt || data.lastClosedCase.updatedAt)}. ` : ''}Adding a note, assigning yourself or scheduling a follow-up opens one.`}
        />
      )}

      {c && (
        <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
          <div>
            <div style={label}>Status</div>
            <Select
              style={{ width: '100%' }}
              value={c.status}
              options={STATUS_OPTIONS}
              disabled={saving}
              onChange={(status) => update({ status }, 'Status updated')}
            />
          </div>
          <div>
            <div style={label}>Priority</div>
            <Select
              style={{ width: '100%' }}
              value={c.priority}
              options={PRIORITY_OPTIONS}
              disabled={saving}
              onChange={(priority) => update({ priority }, 'Priority updated')}
            />
          </div>
          <div>
            <div style={label}>Assigned to</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32 }}>
              <span>{c.assignedTo?.staffName || 'Unassigned'}</span>
              {assignedToMe
                ? <Button size="small" disabled={saving} onClick={() => update({ assignToMe: false }, 'Unassigned')}>Unassign</Button>
                : <Button size="small" icon={<FontAwesomeIcon icon={faUserCheck} />} disabled={saving} onClick={() => update({ assignToMe: true }, 'Assigned to you')}>Assign to me</Button>}
            </div>
          </div>
          <div>
            <div style={label}>Case</div>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              <div>{c.createdReason}</div>
              <div style={{ color: COLORS.textLight }}>Opened {fmtDay(c.createdAt)}</div>
              {c.callQueue && (
                <Tag color="cyan" style={{ marginTop: 4 }}>
                  <FontAwesomeIcon icon={faPhoneVolume} style={{ marginRight: 6 }} />
                  In call queue{c.callQueue.callCount ? ` · ${c.callQueue.callCount} call${c.callQueue.callCount === 1 ? '' : 's'}` : ''}
                </Tag>
              )}
            </div>
          </div>
        </div>
      )}
      {!c && (
        <Button icon={<FontAwesomeIcon icon={faUserCheck} />} disabled={saving} onClick={() => update({ assignToMe: true }, 'Case opened and assigned to you')}>
          Open a case and assign to me
        </Button>
      )}
      </Section>

      <Section icon={faCalendarCheck} title="Follow-up">
      {pendingFollowUp && (
        <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ fontWeight: 600 }}>Due {fmtDay(pendingFollowUp.scheduledDate)}</div>
            {pendingFollowUp.reason && <div style={{ fontSize: 13, color: COLORS.textLight }}>{pendingFollowUp.reason}</div>}
          </div>
          <Button type="primary" disabled={saving} onClick={() => update({ followUpDone: true }, 'Follow-up done')}>Mark done</Button>
        </div>
      )}
      {c?.followUp?.completed && (
        <div style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 12 }}>
          Last follow-up completed {fmtDay(c.followUp.completedDate)}{c.followUp.completedBy ? ` by ${c.followUp.completedBy}` : ''}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <DatePicker
          value={followUpDate}
          onChange={setFollowUpDate}
          disabledDate={(d) => d && d.isBefore(dayjs().startOf('day'))}
          placeholder="Follow-up date"
        />
        <Input
          style={{ flex: 1, minWidth: 200 }}
          placeholder="Reason (optional)"
          maxLength={500}
          value={followUpReason}
          onChange={(e) => setFollowUpReason(e.target.value)}
        />
        <Button disabled={!followUpDate || saving} onClick={scheduleFollowUp}>
          {pendingFollowUp ? 'Reschedule' : 'Schedule'}
        </Button>
      </div>
      </Section>

      <Section icon={faNoteSticky} title="Notes">
      <TextArea
        rows={3}
        maxLength={5000}
        placeholder="What happened? Who did you talk to? What's next?"
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
        <Select style={{ width: 140 }} value={noteType} options={NOTE_TYPES} onChange={setNoteType} />
        <Button type="primary" disabled={!noteText.trim() || saving} loading={saving} onClick={addNote}>Add note</Button>
      </div>

      <div style={{ marginTop: 20 }}>
        {data.notes.length ? data.notes.map((n) => (
          <div key={`${n.caseId}-${n._id}`} style={{ padding: '14px 0', borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, fontSize: 12, color: COLORS.textLight }}>
              <Tag style={{ marginInlineEnd: 0 }}>{NOTE_LABELS[n.noteType] || 'Note'}</Tag>
              <span style={{ fontWeight: 500, color: COLORS.textMedium }}>{n.by}</span>
              <span>· {fmt(n.at)}</span>
            </div>
            <div style={{ marginTop: 8, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55, color: COLORS.textDark }}>{n.text}</div>
          </div>
        )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notes yet" />}
      </div>
      </Section>
    </div>
  );
};

export default CaseTab;
